import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, Loader2, Clock } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createBooking, createCheckout } from "@/lib/apiClient";

const TIMES = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export const Booking = ({ services, selected, setSelected, nonprofitPreset }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [isNonprofit, setIsNonprofit] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (nonprofitPreset) setIsNonprofit(true);
  }, [nonprofitPreset]);

  const service = services.find((s) => s.id === selected);
  const freeForNp = isNonprofit && service && ["Classes", "Community"].includes(service.category);
  const needsPayment = service && service.paid && !freeForNp;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return toast.error("Please choose a service");
    if (!form.name || !form.email) return toast.error("Name and email are required");
    if (!date) return toast.error("Please pick a preferred date");

    setSubmitting(true);
    try {
      const booking = await createBooking({
        name: form.name,
        email: form.email,
        phone: form.phone,
        service_id: selected,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time || null,
        is_nonprofit: isNonprofit,
        message: form.message,
      });

      if (booking.payment_status === "pending") {
        toast.success("Booking created — redirecting to secure checkout...");
        const { checkout_url } = await createCheckout(booking.id);
        window.location.href = checkout_url;
        return;
      }
      toast.success("Request sent! Check your email for confirmation.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="booking" data-testid="booking-section" className="py-28 sm:py-40 bg-[#0A0A0A]">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-body text-xs uppercase tracking-[0.3em] text-gray-500 mb-4">Let&apos;s Create</p>
            <h2 className="font-head uppercase text-4xl sm:text-6xl tracking-tighter text-white leading-none">
              Book a<br />Service<span className="text-[#D90429]">.</span>
            </h2>
            <p className="font-body text-base text-gray-400 leading-relaxed mt-8 max-w-md">
              Pick a service and a preferred date. Paid sessions and classes are booked instantly through secure
              checkout. Custom video and photography requests are confirmed personally by Darrel.
            </p>
            {service && (
              <div className="mt-10 border border-white/10 p-6 bg-[#0f0f10]" data-testid="booking-summary">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-gray-500">Selected</p>
                <p className="font-head uppercase text-2xl text-white tracking-tight mt-2">{service.name}</p>
                <p className="font-body text-sm text-gray-400 mt-2">{service.description}</p>
                <p className="font-head text-3xl text-[#EF233C] mt-4">
                  {freeForNp ? "FREE" : service.paid ? `$${service.price.toFixed(0)}` : "Custom Quote"}
                </p>
              </div>
            )}
          </div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            onSubmit={handleSubmit}
            data-testid="booking-form"
            className="lg:col-span-7 border border-white/10 bg-[#0f0f10] p-8 sm:p-10 space-y-6"
          >
            <div className="space-y-2">
              <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Service</Label>
              <Select value={selected || ""} onValueChange={setSelected}>
                <SelectTrigger data-testid="booking-service-select" className="bg-black/40 border-white/15 text-white h-12">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent className="bg-[#0f0f10] border-white/15 text-white">
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`booking-service-option-${s.id}`}>
                      {s.name} {s.paid ? `— $${s.price.toFixed(0)}` : "— Quote"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Full Name</Label>
                <Input data-testid="booking-name" value={form.name} onChange={set("name")} placeholder="Your name" className="bg-black/40 border-white/15 text-white h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Email</Label>
                <Input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="bg-black/40 border-white/15 text-white h-12" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Phone (optional)</Label>
                <Input data-testid="booking-phone" value={form.phone} onChange={set("phone")} placeholder="+1 ..." className="bg-black/40 border-white/15 text-white h-12" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Preferred Time</Label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="booking-time-select" className="bg-black/40 border-white/15 text-white h-12">
                    <SelectValue placeholder={<span className="flex items-center gap-2"><Clock className="w-4 h-4" /> Select time</span>} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f10] border-white/15 text-white">
                    {TIMES.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`booking-time-${t}`}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Preferred Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="booking-date-trigger"
                    className="w-full h-12 justify-start bg-black/40 border-white/15 text-white hover:bg-black/60 hover:text-white font-normal"
                  >
                    <CalendarIcon className="mr-3 h-4 w-4 text-[#EF233C]" />
                    {date ? format(date, "PPP") : <span className="text-gray-500">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-[#0f0f10] border-white/15" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300 uppercase text-xs tracking-[0.15em]">Project Details</Label>
              <Textarea data-testid="booking-message" value={form.message} onChange={set("message")} placeholder="Tell me about your project, vision or goals..." className="bg-black/40 border-white/15 text-white min-h-[110px]" />
            </div>

            <div className="flex items-center gap-3">
              <Checkbox id="np" data-testid="booking-nonprofit" checked={isNonprofit} onCheckedChange={setIsNonprofit} className="border-white/30 data-[state=checked]:bg-[#D90429] data-[state=checked]:border-[#D90429]" />
              <Label htmlFor="np" className="text-gray-300 text-sm cursor-pointer">
                I represent a registered non-profit organization
              </Label>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit"
              className="w-full h-14 bg-[#D90429] hover:bg-[#EF233C] text-white uppercase tracking-[0.15em] text-sm rounded-none"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
              ) : needsPayment ? (
                `Book & Pay $${service.price.toFixed(0)}`
              ) : (
                "Send Request"
              )}
            </Button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
